// src/layout/AppLayout.js
import React, { useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Stack,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link, useLocation } from "react-router-dom";

import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import SettingsBrightnessOutlinedIcon from "@mui/icons-material/SettingsBrightnessOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import { useThemeMode } from "../theme/ThemeModeProvider";
import { useAuth } from "../auth/AuthProvider";

const NavBtn = ({ to, label }) => {
  const { pathname } = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const active = pathname === to;

  return (
    <Button
      component={Link}
      to={to}
      sx={{
        borderRadius: 999,
        px: 2,
        py: 0.8,
        minHeight: 34,
        textTransform: "none",
        fontWeight: 900,
        letterSpacing: 0.2,

        color: active
          ? isDark
            ? "rgba(255,255,255,0.95)"
            : "rgba(2,6,23,0.92)"
          : isDark
          ? "rgba(255,255,255,0.78)"
          : "rgba(2,6,23,0.72)",

        border: active
          ? isDark
            ? "1px solid rgba(255,255,255,0.22)"
            : "1px solid rgba(2,6,23,0.14)"
          : isDark
          ? "1px solid rgba(255,255,255,0.10)"
          : "1px solid rgba(2,6,23,0.10)",

        background: active
          ? isDark
            ? "linear-gradient(135deg, rgba(167,139,250,0.75), rgba(96,165,250,0.55))"
            : "linear-gradient(135deg, rgba(2,6,23,0.10), rgba(2,6,23,0.04))"
          : "transparent",

        "&:hover": {
          background: active
            ? isDark
              ? "linear-gradient(135deg, rgba(167,139,250,0.85), rgba(96,165,250,0.65))"
              : "linear-gradient(135deg, rgba(2,6,23,0.14), rgba(2,6,23,0.06))"
            : isDark
            ? "rgba(255,255,255,0.08)"
            : "rgba(2,6,23,0.06)",
        },
      }}
    >
      {label}
    </Button>
  );
};

export default function AppLayout({ children }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { user, logout } = useAuth();

  // from your ThemeModeProvider: pref/mode/toggle/setLight/setDark/setSystem
  const { pref, toggle, setLight, setDark, setSystem } = useThemeMode();

  const [menuEl, setMenuEl] = useState(null);
  const menuOpen = Boolean(menuEl);

  const icon =
    pref === "system" ? (
      <SettingsBrightnessOutlinedIcon />
    ) : isDark ? (
      <DarkModeOutlinedIcon />
    ) : (
      <LightModeOutlinedIcon />
    );

  const metalAppBarBg = isDark
    ? // Dark gunmetal
      "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02)), linear-gradient(135deg, rgba(15,23,42,0.92), rgba(2,6,23,0.92))"
    : // Light silver
      "linear-gradient(180deg, rgba(255,255,255,0.85), rgba(255,255,255,0.60)), linear-gradient(135deg, rgba(226,232,240,0.90), rgba(241,245,249,0.75))";

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden", // ✅ keep as-is
      }}
    >
      {/* ✅ ONLY APP BAR area styled + theme switch */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          borderBottom: isDark
            ? "1px solid rgba(255,255,255,0.12)"
            : "1px solid rgba(2,6,23,0.10)",
          background: metalAppBarBg,
          backdropFilter: "blur(12px)",
          boxShadow: isDark
            ? "inset 0 -1px 0 rgba(255,255,255,0.06), 0 18px 45px rgba(0,0,0,0.45)"
            : "inset 0 -1px 0 rgba(2,6,23,0.06), 0 10px 28px rgba(2,6,23,0.12)",
          position: "relative",
          overflow: "hidden",

          // brushed texture
          "&:before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "repeating-linear-gradient(135deg, rgba(255,255,255,0.10) 0px, rgba(255,255,255,0.10) 1px, rgba(255,255,255,0.00) 9px, rgba(255,255,255,0.00) 14px)",
            opacity: isDark ? 0.18 : 0.28,
            pointerEvents: "none",
          },

          // specular highlight
          "&:after": {
            content: '""',
            position: "absolute",
            top: -70,
            left: -120,
            width: 380,
            height: 220,
            background: isDark
              ? "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.22), rgba(255,255,255,0.00) 65%)"
              : "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.95), rgba(255,255,255,0.00) 65%)",
            opacity: isDark ? 0.55 : 0.45,
            transform: "rotate(10deg)",
            pointerEvents: "none",
          },
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Typography
            sx={{
              fontWeight: 950,
              letterSpacing: 0.6,
              color: isDark ? "transparent" : "rgba(2,6,23,0.92)",
              background: isDark
                ? "linear-gradient(180deg, rgba(255,255,255,0.95), rgba(255,255,255,0.55))"
                : "none",
              WebkitBackgroundClip: isDark ? "text" : "initial",
              WebkitTextFillColor: isDark ? "transparent" : "initial",
            }}
          >
            Passivest
          </Typography>

          <Stack direction="row" spacing={1}>
            <NavBtn to="/admin" label="Dashboard" />
            <NavBtn to="/admin/masters" label="Masters" />
            <NavBtn to="/admin/daily-data" label="Daily Data" />
            <NavBtn to="/admin/individual-data" label="Individual Data" />
          </Stack>

          <Box sx={{ flex: 1 }} />

          {/* Theme toggle + menu (AppBar only) */}
          <Stack direction="row" spacing={0.6} alignItems="center">
            {user ? (
              <Button
                onClick={logout}
                sx={{
                  borderRadius: 999,
                  border: isDark
                    ? "1px solid rgba(255,255,255,0.14)"
                    : "1px solid rgba(2,6,23,0.12)",
                  color: isDark ? "rgba(255,255,255,0.85)" : "rgba(2,6,23,0.8)",
                }}
              >
                Sign out
              </Button>
            ) : null}
            <Tooltip title="Toggle theme">
              <IconButton
                onClick={toggle}
                sx={{
                  borderRadius: 999,
                  border: isDark
                    ? "1px solid rgba(255,255,255,0.14)"
                    : "1px solid rgba(2,6,23,0.12)",
                  background: isDark
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(2,6,23,0.04)",
                  "&:hover": {
                    background: isDark
                      ? "rgba(255,255,255,0.12)"
                      : "rgba(2,6,23,0.08)",
                  },
                }}
              >
                {icon}
              </IconButton>
            </Tooltip>

            <Tooltip title="Theme options">
              <IconButton
                onClick={(e) => setMenuEl(e.currentTarget)}
                sx={{
                  borderRadius: 999,
                  border: isDark
                    ? "1px solid rgba(255,255,255,0.14)"
                    : "1px solid rgba(2,6,23,0.12)",
                  background: isDark
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(2,6,23,0.04)",
                  "&:hover": {
                    background: isDark
                      ? "rgba(255,255,255,0.12)"
                      : "rgba(2,6,23,0.08)",
                  },
                }}
              >
                <KeyboardArrowDownRoundedIcon />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={menuEl}
              open={menuOpen}
              onClose={() => setMenuEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  minWidth: 180,
                  borderRadius: 2,
                  mt: 1,
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  setSystem();
                  setMenuEl(null);
                }}
              >
                <Box sx={{ width: 22, mr: 1 }}>
                  {pref === "system" ? <CheckRoundedIcon fontSize="small" /> : null}
                </Box>
                System
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setLight();
                  setMenuEl(null);
                }}
              >
                <Box sx={{ width: 22, mr: 1 }}>
                  {pref === "light" ? <CheckRoundedIcon fontSize="small" /> : null}
                </Box>
                Light
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setDark();
                  setMenuEl(null);
                }}
              >
                <Box sx={{ width: 22, mr: 1 }}>
                  {pref === "dark" ? <CheckRoundedIcon fontSize="small" /> : null}
                </Box>
                Dark
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  toggle();
                  setMenuEl(null);
                }}
              >
                Quick toggle
              </MenuItem>
            </Menu>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* ✅ keep rest EXACT (scroll hidden) */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
